<template>
  <div
    class="flex items-center justify-between space-x-4 py-6 pr-4"
    data-cy="generate-link-row"
  >
    <div class="left-col flex shrink items-center">
      <icon-base :icon="data.icon" height="55" width="55" class="mr-4" />
      <div class="flex flex-col">
        <lf-h4>{{ data.title }}</lf-h4>
        <span class="text-xs text-gray-500">{{ data.description }}</span>
      </div>
    </div>
    <div class="right-col flex flex-wrap items-center justify-end space-x-2">
      <lf-primary-button-with-dropdown
        variant="link"
        :button-options="{ class: 'h-10 rounded-l! bg-surface-user-blue!' }"
        :toggle-options="{
          class:
            'w-10! h-10 rounded-r! bg-surface-user-blue! border-l border-primary/[0.12] hover:bg-blue-200/50!',
          tooltip: $t('DEALS.APPLICATION_LINKS.CONFIGURE_LINK_SETTINGS'),
          externalOnly: true,
        }"
        data-cy="generate-link-button"
        @button:click="generateLink"
        @toggle:dropdown="showModal = true"
      >
        <template #button>
          {{ $t("COMMON.COPY_LINK") }}
        </template>
        <template #toggle-icon>
          <icon-base icon="arrow-right" width="12" height="12" class="-mt-1" />
        </template>
      </lf-primary-button-with-dropdown>
    </div>

    <generate-link-modal
      v-if="showModal"
      :application-id="String(applicationData.id)"
      :show-owner-selector="false"
      :show-bp-template-selector="!!data.showBorrowerPlatformTemplate"
      :show-expiration="!!data.showExpiration"
      :generate-action="data.action"
      :additional-payload="additionalPayload"
      :success-message="successText"
      @close="showModal = false"
    >
      <template v-if="hasContractOptions" #additional-options>
        <lf-dropdown
          v-if="data.showSelectWidget"
          :value="selectedWidgetTypeId"
          :placeholder="$t('INTEGRATIONS.WIDGET_NAME')"
          :options="productTypeOptions"
          name="widgetType"
          data-cy="widget-type-dropdown"
          full-width
          @change="selectedWidgetTypeId = String($event)"
        />
        <lf-composite-dropdown
          v-if="data.type === APPLICATION_LINK_TYPE_CONTRACT"
          v-model="selectedContractTemplateOption"
          full-width
          dropdown-class-names="z-[101]"
          :placeholder="compositePlaceholder"
          data-cy="contract-template-dropdown"
          :option-sources="
            compact([
              data.showConsentTemplate && {
                title: t('CUSTOMIZE.CONSENT.TITLE', 2),
                id: 'consentTemplate',
                options: (params: GetConsentTemplatesParams) =>
                  consentTemplateService.getAllTemplates({
                    client_id: client?.id,
                    ...params,
                  }),
              },
              {
                title: showLegacy
                  ? t('DEALS.APPLICATION_LINKS.DOCUSIGN_TEMPLATE')
                  : t('INTEGRATIONS.CONTRACT', 2),
                id: 'docusignTemplate',
                options: showLegacy
                  ? legacyContractTemplates
                  : (params: { page: number; search: string }) => {
                      return getContractTemplates({
                        clients: client?.id ? [client?.id] : undefined,
                        status: 2,
                        ...params,
                      });
                    },
              },
            ])
          "
          show-tooltip
        >
          <template #selected="{ value }">
            <span v-if="value.label">
              {{ value.label }}
              <icon-base
                v-if="isLendflowContractTemplate(value.id)"
                v-tooltip="`${$t('COMMON.LENDFLOW')} ${$t('COMMON.TEMPLATE')}`"
                :icon="IconLendflow"
                view-box="14 12 26 26"
                class="text-green-500"
              />
            </span>
          </template>
          <template #option="{ option, key }">
            <div class="flex items-center space-x-2">
              <span>{{ option.name || option }}</span>
              <icon-base
                v-if="isLendflowContractTemplate(key, option)"
                v-tooltip="`${$t('COMMON.LENDFLOW')} ${$t('COMMON.TEMPLATE')}`"
                :icon="IconLendflow"
                view-box="14 12 26 26"
                class="text-green-500"
              />
            </div>
          </template>
        </lf-composite-dropdown>
      </template>
    </generate-link-modal>
  </div>
</template>
<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";

import compact from "lodash/compact";
import pick from "lodash/pick";

import { AnalyticsEvent } from "@/enums/analytics";
import {
  APPLICATION_LINK_TYPE_CONTRACT,
  APPLICATION_SOURCE,
} from "@/helpers/constants";
import { useIntegration } from "@/hooks/auth";
import { useClipboardWithMessage } from "@/hooks/clipboard";
import { useContractBuilder } from "@/hooks/contractBuilder";
import { useDeals } from "@/hooks/deals";
import applicationsApiService from "@/services/modules/applications";
import consentTemplateService, {
  type GetConsentTemplatesParams,
} from "@/services/modules/consentTemplate";
import { useAnalyticsStore } from "@/stores/analytics";
import { useContractBuilderStore } from "@/stores/contractBuilder";

import GenerateLinkModal from "@/components/deals/bp-link-generator/GenerateLinkModal.vue";
import IconLendflow from "@/components/icons/IconLendflow.vue";
import IconBase from "@/components/ui/IconBase.vue";
import LfPrimaryButtonWithDropdown from "@/components/ui/buttons/LfPrimaryButtonWithDropdown.vue";
import LfCompositeDropdown from "@/components/ui/inputs/LfCompositeDropdown.vue";
import LfDropdown from "@/components/ui/inputs/LfDropdown.vue";

import type { IApplication } from "@/models/applications";
import type {
  CompositeDropdownModel,
  IGenerateLinkData,
} from "@/models/common";

const RESUME_SUPPORTED_BUSINESS_CREDIT_WIDGETS = [4, 8, 26];
const RESUME_SUPPORTED_LENDING_WIDGETS = [1, 9, 10, 18, 19, 27, 30, 32];

const {
  data,
  successText = "",
  showLegacy = false,
  applicationData,
} = defineProps<{
  data: IGenerateLinkData;
  successText?: string;
  showLegacy?: boolean;
  applicationData: IApplication;
}>();

const contractsStore = useContractBuilderStore();
const { trackEvent } = useAnalyticsStore();

const { state, dispatch } = useStore();
const { t } = useI18n();
const { isLendflowContractTemplate } = useContractBuilder();
const copyToClipboard = useClipboardWithMessage();
const { getFilteredProductTypes } = useIntegration();
const { activeDeal } = useDeals();
const { getContractTemplates } = contractsStore;

const selectedContractTemplateOption = ref<CompositeDropdownModel>({
  id: "",
  label: "",
  source: "",
});

const selectedWidgetTypeId = ref(
  applicationData.is_business_credit ? "4" : "1"
);

const showModal = ref(false);

const hasContractOptions = computed(
  () => data.showSelectWidget || data.type === APPLICATION_LINK_TYPE_CONTRACT
);

const additionalPayload = computed(() => {
  const payload: Record<string, unknown> = { ...data.linkData };

  if (data.type === APPLICATION_LINK_TYPE_CONTRACT) {
    if (
      selectedContractTemplateOption.value.id &&
      selectedContractTemplateOption.value.id !== "0"
    ) {
      let targetKey = "consent_template_id";

      if (selectedContractTemplateOption.value.source === "docusignTemplate") {
        targetKey = showLegacy
          ? "docusign_placeholder_id"
          : "contract_template_id";
      }
      payload[targetKey] = selectedContractTemplateOption.value.id;
    }

    if (data.showSelectWidget) {
      payload.product = selectedWidgetTypeId.value;
    }
  }

  return payload;
});

const legacyContractTemplates = computed(() => {
  return state.auth.authClientIntegration?.docusign?.placeholders || {};
});

const client = computed(() => applicationData.client);

const compositePlaceholder = computed(() =>
  data.showConsentTemplate
    ? t("CUSTOMIZE.CONSENT.TITLE")
    : showLegacy
      ? t("DEALS.APPLICATION_LINKS.DOCUSIGN_TEMPLATE")
      : t("INTEGRATIONS.CONTRACT", 2)
);

const productTypeOptions = computed(() => {
  const widgetList = applicationData.is_business_credit
    ? RESUME_SUPPORTED_BUSINESS_CREDIT_WIDGETS
    : RESUME_SUPPORTED_LENDING_WIDGETS;
  const availabeProductTypes = getFilteredProductTypes({ includeNone: false });

  return pick(availabeProductTypes, widgetList);
});

const generateLink = async () => {
  const payload = {
    ...data.linkData,
  };
  if (data.type === APPLICATION_LINK_TYPE_CONTRACT) {
    if (
      selectedContractTemplateOption.value.id &&
      selectedContractTemplateOption.value.id !== "0"
    ) {
      let targetKey = "consent_template_id";

      if (selectedContractTemplateOption.value.source === "docusignTemplate") {
        targetKey = showLegacy
          ? "docusign_placeholder_id"
          : "contract_template_id";
      }
      payload[targetKey] = selectedContractTemplateOption.value.id;
    }
  }
  if (data.showSelectWidget) {
    payload.product = selectedWidgetTypeId.value;
  }

  const link = await dispatch(data.action, payload);
  await copyToClipboard(link, { success: successText });

  if (link) {
    trackEvent(AnalyticsEvent.DealAppLinkGenerated, {
      link_type: data.type,
    });
  }
};

onMounted(async () => {
  if (activeDeal.value.source !== APPLICATION_SOURCE.WIDGET) {
    return;
  }
  const widgetInfo = await applicationsApiService.getWidgetInfo(
    activeDeal.value.id
  );

  if (widgetInfo.consent_template) {
    selectedContractTemplateOption.value = {
      id: widgetInfo.consent_template.id,
      label: widgetInfo.consent_template.name,
      source: "consentTemplate",
    };
  } else if (widgetInfo.contract_template) {
    selectedContractTemplateOption.value = {
      id: widgetInfo.contract_template.id,
      label: widgetInfo.contract_template.name,
      source: "docusignTemplate",
    };
  } else {
    const { data: consentTemplates } =
      await consentTemplateService.getAllTemplates({
        client_id: client.value?.id,
      });
    if (!consentTemplates.length) {
      return;
    }
    const { name, id } = consentTemplates[0];
    selectedContractTemplateOption.value = {
      id,
      label: name,
      source: "consentTemplate",
    };
  }
});
</script>
